/**
 * @spec docs/specs/features/staking-position-ui.md#design-system
 * @figma https://www.figma.com/design/Z2CmEcX1brcgv3hfhOWO07/Phenom-Poker?node-id=10339-18117
 *
 * Button component matching Phenom Poker design system.
 *
 * Variants:
 * - primary (purple): Solid purple background, white text - main CTAs
 * - secondary (grey): Solid grey background - secondary actions (alias for grey)
 * - outline (purple-outlined): Purple border, purple text - tertiary actions
 * - ghost (link-purple): No background, purple text - link-style buttons
 * - grey: Solid grey background (same as secondary)
 * - grey-outlined: Grey border, grey text - subtle actions
 * - link-grey: No background, grey text - muted links
 * - white: White background, dark text - for dark contexts
 * - tether: Solid tether background, white text - USDT actions
 * - tether-outline: Tether border, tether text - secondary USDT actions
 * - tether-ghost: No background, tether text - link-style USDT buttons
 *
 * Sizes:
 * - sm: Small buttons
 * - md: Medium (Figma "medium") - 12px/8px padding
 * - lg: Large (Figma "large") - 16px/12px padding
 */
import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Visual style variant
   * @default 'primary'
   */
  variant?:
    | 'primary'
    | 'secondary'
    | 'outline'
    | 'ghost'
    | 'grey'
    | 'grey-outlined'
    | 'link-grey'
    | 'white'
    | 'tether'
    | 'tether-outline'
    | 'tether-ghost';

  /**
   * Button size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Show loading spinner and disable interaction
   */
  isLoading?: boolean;

  /**
   * Icon to display before the button text
   */
  leftIcon?: ReactNode;

  /**
   * Icon to display after the button text
   */
  rightIcon?: ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading,
      disabled,
      leftIcon,
      rightIcon,
      children,
      ...props
    },
    ref
  ) => {
    // Base styles applied to all buttons
    const baseStyles = cn(
      'inline-flex items-center justify-center',
      'font-bold text-sm leading-5', // Manrope Bold 14px/20px
      'rounded-lg', // 8px border radius
      'transition-[background-color,border-color,color,box-shadow] duration-200 ease-in-out', // Transition colors & shadow, not ring
      'focus:outline-none focus:ring-4', // No offset - ring directly on button edge per Figma
      'cursor-pointer select-none',
      'disabled:cursor-not-allowed'
    );

    // Variant styles matching Figma design system exactly
    // Using dedicated btn-* color tokens from Figma
    const variants = {
      // Solid purple - primary CTA (type=purple in Figma)
      primary: cn(
        'bg-btn-purple text-white',
        'hover:bg-btn-purple-hover',
        'focus:ring-[rgba(160,70,211,0.4)]',
        'disabled:bg-btn-purple-disabled disabled:text-white/10'
      ),

      // Solid grey - secondary actions (type=grey in Figma)
      secondary: cn(
        'bg-btn-grey text-white/50',
        'hover:bg-btn-grey-hover hover:text-white',
        'focus:ring-[rgba(54,49,73,0.4)]',
        'disabled:bg-btn-grey-disabled disabled:text-white/10'
      ),

      // Purple outlined (type=purple-outlined in Figma)
      outline: cn(
        'border border-btn-purple text-btn-purple bg-transparent',
        'hover:bg-btn-purple hover:text-white',
        'focus:ring-[rgba(160,70,211,0.4)]',
        'disabled:border-[rgba(160,70,211,0.3)] disabled:text-[rgba(160,70,211,0.3)]'
      ),

      // Purple link (type=link-purple in Figma)
      ghost: cn(
        'text-btn-purple bg-transparent',
        'hover:text-btn-purple-hover',
        'focus:ring-[rgba(160,70,211,0.4)]',
        'disabled:text-[rgba(160,70,211,0.3)]'
      ),

      // Solid grey (alias for secondary - type=grey in Figma)
      grey: cn(
        'bg-btn-grey text-white/50',
        'hover:bg-btn-grey-hover hover:text-white',
        'focus:ring-[rgba(54,49,73,0.4)]',
        'disabled:bg-btn-grey-disabled disabled:text-white/10'
      ),

      // Grey outlined (type=grey-outlined in Figma)
      'grey-outlined': cn(
        'border border-white/10 text-white/50 bg-transparent',
        'hover:border-white/20 hover:text-white',
        'focus:ring-[rgba(54,49,73,0.4)]',
        'disabled:border-white/10 disabled:text-white/10'
      ),

      // Grey link (type=link-grey in Figma)
      'link-grey': cn(
        'text-white/50 bg-transparent',
        'hover:text-white',
        'focus:ring-[rgba(54,49,73,0.4)]',
        'disabled:text-white/10'
      ),

      // White solid (type=white in Figma)
      white: cn(
        'bg-white text-bg-primary',
        'hover:bg-white/90 hover:text-btn-purple hover:shadow-[inset_2px_2px_2px_0px_rgba(0,0,0,0.25)]',
        'focus:ring-white/40',
        'disabled:bg-white/30 disabled:text-white/10'
      ),

      // Solid tether (type=tether in Figma)
      tether: cn(
        'bg-btn-tether text-white',
        'hover:bg-btn-tether-hover',
        'focus:ring-[rgba(0,147,147,0.4)]',
        'disabled:bg-btn-tether-disabled disabled:text-white/10'
      ),

      // Tether outlined (type=tether-outlined in Figma)
      'tether-outline': cn(
        'border border-btn-tether text-btn-tether bg-transparent',
        'hover:bg-btn-tether hover:text-white',
        'focus:ring-[rgba(0,147,147,0.4)]',
        'disabled:border-[rgba(0,147,147,0.3)] disabled:text-[rgba(0,147,147,0.3)]'
      ),

      // Tether link (type=link-tether in Figma)
      'tether-ghost': cn(
        'text-btn-tether bg-transparent',
        'hover:text-btn-tether-hover',
        'focus:ring-[rgba(0,147,147,0.4)]',
        'disabled:text-[rgba(0,147,147,0.3)]'
      ),
    };

    // Size styles (padding and icon gap)
    const sizes = {
      sm: 'px-2.5 py-1.5 gap-1.5', // Smaller for compact UIs
      md: 'px-3 py-2 gap-1.5', // Figma "medium": 12px/8px padding, 6px gap
      lg: 'px-4 py-3 gap-2', // Figma "large": 16px/12px padding, 8px gap
    };

    // Icon sizes based on button size
    const iconSizes = {
      sm: 'size-4', // 16px
      md: 'size-4', // 16px (Figma medium)
      lg: 'size-5', // 20px (Figma large)
    };

    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={cn(
          baseStyles,
          variants[variant],
          sizes[size],
          isLoading && 'relative text-transparent',
          className
        )}
        {...props}
      >
        {/* Loading spinner overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          </div>
        )}

        {/* Left icon */}
        {leftIcon && !isLoading && (
          <span className={cn('shrink-0', iconSizes[size])}>{leftIcon}</span>
        )}

        {/* Button text */}
        {children}

        {/* Right icon */}
        {rightIcon && !isLoading && (
          <span className={cn('shrink-0', iconSizes[size])}>{rightIcon}</span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button };
